import { Component, OnDestroy, OnInit } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LotAttribute } from '../../shared/inventory/model/lot.model';
import { LotService } from '../../shared/inventory/service/lot.service';
import { VariableTypeEnum } from '../../shared/ontology/variable-type.enum';
import { LotAttributeContext } from './attributes/lot-attribute.context';
import { VariableDetails } from '../../shared/ontology/model/variable-details';
import { VariableService } from '../../shared/ontology/service/variable.service';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { VariableValidationService } from '../../shared/ontology/service/variable-validation.service';
import { FileService } from '../../shared/file/service/file.service';
import { MI_MANAGE_FILES_PERMISSION } from '../../shared/auth/permissions';
import { Principal } from '../../shared';
import { FileDeleteOptionsComponent } from '../../shared/file/component/file-delete-options.component';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { AlertService } from '../../shared/alert/alert.service';
import { MAX_ATTRIBUTE_DISPLAY_SIZE } from '../../app.constants';
import { LotAttributeModalComponent } from './attributes/lot-attribute-modal.component';
import { LotDetailPopupService } from './lot-detail-popup.service';
import { LotDetailContext } from './lot-detail.context';

@Component({
    selector: 'jhi-lot-attributes-pane',
    templateUrl: './lot-attributes-pane.component.html'
})
export class LotAttributesPaneComponent implements OnInit, OnDestroy {

    protected readonly MAX_ATTRIBUTE_DISPLAY_SIZE = MAX_ATTRIBUTE_DISPLAY_SIZE;

    attributes: LotAttribute[] = [];
    isFileStorageConfigured: boolean;

    variableByAttributeId: { [key: number]: VariableDetails } = {};
    subscriptionManager = new SubscriptionsManager();

    constructor(private router: Router,
                public languageservice: JhiLanguageService,
                public translateService: TranslateService,
                private eventManager: JhiEventManager,
                private lotDetailContext: LotDetailContext,
                private lotService: LotService,
                private variableService: VariableService,
                private variableValidationService: VariableValidationService,
                private alertService: AlertService,
                private modalService: NgbModal,
                private lotAttributeContext: LotAttributeContext,
                private fileService: FileService,
                private principal: Principal,
                private lotDetailPopupService: LotDetailPopupService
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.loadAttributes();
        this.registerLotAttributeChanged();
        this.fileService.isFileStorageConfigured().then((isFileStorageConfigured) => this.isFileStorageConfigured = isFileStorageConfigured);
    }

    registerLotAttributeChanged() {
        this.subscriptionManager.add = this.eventManager.subscribe('attributeChanged', (event) => {
            this.loadAttributes();
            this.lotDetailContext.notifyLotDetailChanges();
        });
    }

    async loadAttributes() {
        const lotId = this.lotDetailContext.lotId;
        this.attributes = await this.lotService.getLotAttributes(lotId + '').toPromise();

        // Get extra info not available in the attribute entity (e.g valid values)
        const attributesByVariableId: { [key: number]: LotAttribute } =
            [...this.attributes]
                .reduce((prev: any, attribute) => (prev[attribute.variableId] = attribute, prev), {});
        this.variableByAttributeId = await this.variableService.filterVariables({ variableIds: Object.keys(attributesByVariableId),
            showObsoletes: true })
            .toPromise().then((variables) => {
                return variables.reduce((prev: any, variable) => {
                    prev[attributesByVariableId[Number(variable.id)].id] = variable;
                    return prev;
                }, {});
            });
    }

    createLotAttribute(): void {
        this.lotDetailPopupService.close();
        const modalRef = this.modalService.open(LotAttributeModalComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
        modalRef.componentInstance.lotId = this.lotDetailContext.lotId;
        modalRef.result.then((result) => {
            this.lotDetailPopupService.openLotDetail(this.lotDetailContext.lotUUID, 'attributes');
        }, () => {
            this.lotDetailPopupService.openLotDetail(this.lotDetailContext.lotUUID, 'attributes');
        });
    }

    editLotAttribute(lotAttribute: LotAttribute): void {
        this.lotDetailPopupService.close();
        this.lotAttributeContext.variable = this.variableByAttributeId[lotAttribute.id];
        this.lotAttributeContext.attributeType = VariableTypeEnum.INVENTORY_ATTRIBUTE;
        this.lotAttributeContext.attribute = lotAttribute;
        const modalRef = this.modalService.open(LotAttributeModalComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
        modalRef.componentInstance.lotId = this.lotDetailContext.lotId;
        modalRef.result.then((result) => {
            this.lotDetailPopupService.openLotDetail(this.lotDetailContext.lotUUID, 'attributes');
        }, () => {
            this.lotDetailPopupService.openLotDetail(this.lotDetailContext.lotUUID, 'attributes');
        });
    }

    isValidValue(attribute: LotAttribute) {
        const variable = this.variableByAttributeId[attribute.id];
        const validationStatus = this.variableValidationService.isValidValue(attribute.value, variable);
        return validationStatus.isValid && validationStatus.isInRange;
    }

    gotoFiles(attribute: LotAttribute) {
        this.navigateToTab(this.lotDetailContext.lotUUID, 'files', { variableName:  attribute.variableName });
    }

    async deleteLotAttribute(lotAttribute: LotAttribute) {
        const variableIds = [lotAttribute.variableId];
        const fileCountResp = await this.fileService.getFileCount(variableIds, null, this.lotDetailContext.lotId).toPromise();
        const fileCount = Number(fileCountResp.headers.get('X-Total-Count'));
        if (fileCount > 0 && !await this.principal.hasAnyAuthority(MI_MANAGE_FILES_PERMISSION)) {
            this.alertService.error('lot-attribute-modal.delete.blocked.files');
            return;
        }

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('lot-attribute-modal.delete.warning', { param: lotAttribute.variableName });

        try {
            await confirmModalRef.result;
        } catch (e) {
            return;
        }

        try {
            if (fileCount > 0) {
                const fileOptionsModal = this.modalService.open(FileDeleteOptionsComponent as Component);
                fileOptionsModal.componentInstance.fileCount = fileCount;
                let doRemoveFiles;
                try {
                    doRemoveFiles = await fileOptionsModal.result;
                } catch (e) {
                    return;
                }
                if (doRemoveFiles) {
                    await this.fileService.removeFiles(variableIds, null, this.lotDetailContext.lotId).toPromise();
                } else {
                    await this.fileService.detachFiles(variableIds, null, this.lotDetailContext.lotId).toPromise();
                }
            }

            await this.lotService.deleteLotAttribute(this.lotDetailContext.lotId, lotAttribute.id).toPromise();
            this.alertService.success('lot-attribute-modal.delete.success');
            this.loadAttributes();
        } catch (response) {
        }
    }

    navigateToTab(lotUUID: string, route: string, params?: { [key: string]: string }) {
        this.router.navigate([{
            outlets: {
                'lotDetailPopup': ['lot-detail-main', lotUUID, {
                    outlets: {
                        'lot-detail-outlet': [route]
                    }
                }]
            }
        }], { queryParamsHandling: 'merge', queryParams: params });
    }

}
